import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, RefreshCw, MapPin, Ticket, Plus } from 'lucide-react';
import apiService from '../services/apiService';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import KalenderTicketKennung, { kennungPlatz, hatKennung } from './kalender/KalenderTicketKennung';
import { GebuchtMenuePunkt, useGebuchtUmschalten } from './kalender/Gebucht';
import EventToTicketModal from './EventToTicketModal';
import { useToast } from './ToastProvider';
import TicketCustomerField from './TicketCustomerField';
import CalendarTicketTitle from './CalendarTicketTitle';
import * as loadingDebug from '../utils/loadingDebug';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import { flagInvalidField } from '../utils/formValidation';
import { useConfirm } from './ui/ConfirmDialog';
import { erstelleKalenderHelfer } from '../utils/kalenderHelfer';

// Narrow, single-day calendar panel intended to sit left of TicketDetail
// Can also be embedded in dashboards via isEmbedded=true
// Reuses core positioning/drag/resize logic from CalendarView, simplified to one day
const DayCalendarPanel = ({ 
  date, 
  currentUser, 
  highlightTicketId = null, 
  highlightTicketNumber = null, 
  isEmbedded = false, 
  onOpenTicket = null, 
  onOpenEvent = null,
  linkedCalendarEventIds = [],
  calendarEventTicketStatus = {}
}) => {
  const { pushToast } = useToast();
  // #604: „Gebucht“ aus dem Kontextmenü, danach den Tag still neu laden
  const gebuchtUmschalten = useGebuchtUmschalten(async () => {
    await loadDayDataSilently();
  });
  const { t, getLocale } = useI18n();
  const confirm = useConfirm();

  // Core state
  const [events, setEvents] = useState([]);
  const [plannedTickets, setPlannedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Dynamic time window (defaults to 8-18 like CalendarView)
  const [timeStartHour, setTimeStartHour] = useState(8);
  const [timeEndHour, setTimeEndHour] = useState(18);
  // #311: die gemeinsamen Kalender-Helfer aus utils/kalenderHelfer.js.
  // Arbeitszeitfenster und Sprache sind gebunden, damit die Aufrufstellen
  // unveraendert bleiben.
  const {
    getUserTimeZone, formatTime, snapToGrid, parseBlockSuffix,
    getEventStatus, getTicketColor, getEventHeight, getTicketHeight,
    getEventPosition, getTicketPosition, getCurrentTimePosition, calculateItemPositions,
    getTimeSlots, isToday, tagesEintraege
  } = useMemo(
    () => erstelleKalenderHelfer({ timeStartHour, timeEndHour, locale: getLocale() }),
    [timeStartHour, timeEndHour, getLocale]
  );


  // Drag/resize state (planned tickets only)
  const [draggedPlannedTicket, setDraggedPlannedTicket] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [showDragPreview, setShowDragPreview] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [operationMessage, setOperationMessage] = useState('');
  const [resizeMode, setResizeMode] = useState(false);
  const [, setResizingTicket] = useState(null);
  const [, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [showResizePreview, setShowResizePreview] = useState(false);
  const [justResized, setJustResized] = useState(false);
  const currentResizeValues = useRef({ startDateTime: null, endDateTime: null });
  const resizeMouseMoveRef = useRef(null);
  const resizeMouseUpRef = useRef(null);
  // Auswahl-Rechteck (Zeitbereich per Maus ziehen)
  const selectionMouseMoveRef = useRef(null);
  const selectionMouseUpRef = useRef(null);
  const selectionContainerRectRef = useRef(null);
  const selectionStartYRef = useRef(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null); // { topPercent, heightPercent }
  const [pendingPlannedTimeRange, setPendingPlannedTimeRange] = useState(null); // { startDateTime, endDateTime }
  // Ticket-Erstellung nach Auswahl
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: '',
    tags: [],
    calenderMarker: null,
    customerId: null,
    contactId: null
  });
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, ticket: null });
  const [dropChoiceModal, setDropChoiceModal] = useState({ visible: false, context: null });
  // Context-Menu für Graph-Termine
  const [eventContextMenu, setEventContextMenu] = useState({ visible: false, x: 0, y: 0, event: null });
  const [showEventConvertModal, setShowEventConvertModal] = useState(false);
  const [selectedEventForConvert, setSelectedEventForConvert] = useState(null);

  const dayStart = useMemo(() => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [date]);

  const dayEnd = useMemo(() => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [date]);

  const dayStartMs = useMemo(() => dayStart.getTime(), [dayStart]);
  const dayEndMs = useMemo(() => dayEnd.getTime(), [dayEnd]);
  const userKey = useMemo(() => (currentUser?.mail || currentUser?.userPrincipalName || currentUser?.username || ''), [currentUser]);

  // Load day data
  const loadDayData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      try {
        loadingDebug.start('day-events', 'Tageskalender: Termine laden', 'o365');
        loadingDebug.start('day-planned', 'Tageskalender: Geplante Tickets laden', 'local');
      } catch (_) {}
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(dayStart, dayEnd),
        apiService.getPlannedTickets(dayStart, dayEnd)
      ]);

      const ev = eventsResponse.value || [];
      const tickets = plannedTicketsResponse.tickets || [];

      // Filter tickets to only those assigned to current user or unassigned (same as CalendarView)
      const filteredTickets = tickets.filter(ticket => {
        if (!currentUser) return true;
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username ||
                                        ticket.assignedTo === currentUser.userPrincipalName ||
                                        ticket.assignedTo === currentUser.mail;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        return isAssignedToCurrentUser || isUnassigned;
      });

      setEvents(ev);
      setPlannedTickets(filteredTickets);
      try {
        loadingDebug.done('day-events', true);
        loadingDebug.done('day-planned', true);
      } catch (_) {}
    } catch (e) {
      console.error('DayCalendarPanel: error loading day data', e);
      setError(t('calendar.errorLoad', 'Fehler beim Laden der Termine.'));
      try {
        loadingDebug.done('day-events', false);
        loadingDebug.done('day-planned', false);
      } catch (_) {}
    } finally {
      setLoading(false);
      try { setTimeout(() => { try { loadingDebug.clearAll(); } catch (_) {} }, 3000); } catch (_) {}
    }
  }, [dayStart, dayEnd, currentUser, t]);

  const loadDayDataSilently = useCallback(async () => {
    try {
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(dayStart, dayEnd),
        apiService.getPlannedTickets(dayStart, dayEnd)
      ]);
      setEvents(eventsResponse.value || []);
      // Keep same filter as above
      const tickets = plannedTicketsResponse.tickets || [];
      const filteredTickets = tickets.filter(ticket => {
        if (!currentUser) return true;
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username ||
                                        ticket.assignedTo === currentUser.userPrincipalName ||
                                        ticket.assignedTo === currentUser.mail;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        return isAssignedToCurrentUser || isUnassigned;
      });
      setPlannedTickets(filteredTickets);
    } catch (e) {
      // silent
    }
  }, [dayStart, dayEnd, currentUser]);

  // #189: zusätzlich contextmenu (Capture) — Rechtsklick feuert kein click,
  // dadurch blieben Ticket- und Termin-Menü beim Objektart-Wechsel offen.
  useEffect(() => {
    if (!contextMenu.visible) return;
    const close = () => setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close, true);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close, true);
    };
  }, [contextMenu.visible]);

  // Close event context menu on outside click
  useEffect(() => {
    if (!eventContextMenu.visible) return;
    const close = () => setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close, true);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close, true);
    };
  }, [eventContextMenu.visible]);

  const handleTicketRightClick = (e, ticket) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, ticket });
  };

  // #119 (W7): auf den ersten freien Arbeitszeit-Slot der nächsten Woche
  // verschieben (Slot-Suche macht der Server, Route /plan-next-week)
  const handleMoveToNextWeek = async () => {
    if (!contextMenu.ticket) return;
    if (operationInProgress) return;
    const ticketId = contextMenu.ticket.originalTicketId || contextMenu.ticket.id;
    setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
    setOperationInProgress(true);
    setOperationMessage(t('contextMenu.movingNextWeek', 'Wird auf nächste Woche verschoben...'));
    try {
      const resp = await apiService.planTicketNextWeek(ticketId);
      const start = resp?.plannedStartDateTime ? new Date(resp.plannedStartDateTime) : null;
      const when = start ? start.toLocaleString([], { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
      pushToast({ message: t('contextMenu.movedNextWeek', `Verschoben auf ${when}`, { date: when }), type: 'success' });
      try {
        await loadDayDataSilently();
      } catch (_) {}
    } catch (error) {
      console.error('Error planning ticket for next week:', error);
      const serverMsg = error?.response?.data?.error;
      pushToast({ message: serverMsg || t('contextMenu.moveNextWeekError', 'Verschieben auf nächste Woche fehlgeschlagen.'), type: 'error' });
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  const handleRemoveFromPlanning = useCallback(async () => {
    if (!contextMenu.ticket) return;
    try {
      setOperationInProgress(true);
      setOperationMessage(t('calendar.updating', 'Wird aktualisiert...'));
      const ticketId = contextMenu.ticket.originalTicketId || contextMenu.ticket.id;
      await apiService.unplanTicket(ticketId);
      setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
      await loadDayDataSilently();
      // Dashboard sofort informieren, damit das Ticket direkt wieder in "Meine"/"Pool" erscheint
      try {
        const enriched = { ...(contextMenu.ticket || {}), id: ticketId, isPlanned: false };
        window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'unplanned_from_day_panel', action: 'unplanned', tickets: [enriched] } }));
      } catch (_) {}
      try {
        setTimeout(() => { try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {} }, 600);
      } catch (_) {}
    } catch (e) {
      console.error('DayCalendarPanel: unplan error', e);
      setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  }, [contextMenu, t, loadDayDataSilently]);

  useEffect(() => { loadDayData(); }, [dayStartMs, dayEndMs, userKey]);

  // Update current time every 10 minutes for marker
  useEffect(() => {
    setCurrentTime(new Date());
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10 * 60 * 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Refresh DayCalendarPanel when dashboard announces updates (e.g., TicketDetail completed)
  useEffect(() => {
    const onDashboardRefresh = async () => {
      try { await loadDayDataSilently(); } catch (_) {}
    };
    window.addEventListener('dashboardEventRefresh', onDashboardRefresh);
    return () => window.removeEventListener('dashboardEventRefresh', onDashboardRefresh);
  }, [loadDayDataSilently]);

  // Listen to dashboard ticket drag events to ensure ghost preview appears even if dataTransfer is blocked
  useEffect(() => {
    const onDashDragStart = (e) => {
      try {
        const d = (e && e.detail) || null;
        if (!d) return;
        const now = new Date();
        const end = new Date(now.getTime() + 60 * 60 * 1000);
        setDraggedPlannedTicket({
          id: `external:${d.id}`,
          plannedStartDateTime: now.toISOString(),
          plannedEndDateTime: end.toISOString(),
          title: d.title || 'Ticket',
          ticketNumber: d.ticketNumber,
          priority: d.priority || 'normal',
          status: d.status || 'offen',
          projectId: d.projectId || null,
          subprojectId: d.subprojectId || null
        });
      } catch (_) {}
    };
    const onDashDragEnd = () => { try { setDraggedPlannedTicket(null); setShowDragPreview(false); } catch(_) {} };
    window.addEventListener('dashboardTicketDragStart', onDashDragStart);
    window.addEventListener('dashboardTicketDragEnd', onDashDragEnd);
    return () => {
      window.removeEventListener('dashboardTicketDragStart', onDashDragStart);
      window.removeEventListener('dashboardTicketDragEnd', onDashDragEnd);
    };
  }, []);

  // Duplicate Graph event filter (simplified reuse of CalendarView approach)
  const filterDuplicateGraphEvents = (items) => {
    const tickets = items.filter(i => i.type === 'ticket');
    const events = items.filter(i => i.type === 'event');
    const calendarEventIds = new Set(tickets.map(i => i.data.calendarEventId).filter(Boolean));
    const ticketNumbers = new Set(tickets.map(i => i.data.ticketNumber));

    // Events to filter: direct id match or subject/body contains ticket reference
    const eventsToFilter = new Set();
    events.forEach(eventItem => {
      const event = eventItem.data;
      if (calendarEventIds.has(event.id)) {
        eventsToFilter.add(event.id);
        return;
      }
      try {
        const body = event.body?.content || '';
        const match = body.match(/\[TICKET:([^\]]+)\]/);
        const ticketNo = match ? match[1] : null;
        if (ticketNo && ticketNumbers.has(ticketNo)) {
          eventsToFilter.add(event.id);
        }
      } catch (_) {}
    });

    return [
      ...tickets,
      ...events.filter(e => !eventsToFilter.has(e.data.id))
    ];
  };

  const cleanupResizeListeners = () => {
    if (resizeMouseMoveRef.current) {
      document.removeEventListener('mousemove', resizeMouseMoveRef.current);
      resizeMouseMoveRef.current = null;
    }
    if (resizeMouseUpRef.current) {
      document.removeEventListener('mouseup', resizeMouseUpRef.current);
      resizeMouseUpRef.current = null;
    }
  };

  useEffect(() => () => cleanupResizeListeners(), []);

  // Cleanup Auswahl-Listener bei Unmount
  useEffect(() => {
    return () => {
      if (selectionMouseMoveRef.current) {
        document.removeEventListener('mousemove', selectionMouseMoveRef.current);
        selectionMouseMoveRef.current = null;
      }
      if (selectionMouseUpRef.current) {
        document.removeEventListener('mouseup', selectionMouseUpRef.current);
        selectionMouseUpRef.current = null;
      }
    };
  }, []);

  const handleResizeStart = (e, ticket, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (operationInProgress) return;
    cleanupResizeListeners();
    setResizeMode(true);
    setResizingTicket(ticket);
    setResizeHandle(handle);
    const startPos = { x: e.clientX, y: e.clientY };
    const core = e.currentTarget.parentElement?.parentElement;
    const parentHeight = core?.offsetHeight || 600;
    currentResizeValues.current = { startDateTime: ticket.plannedStartDateTime, endDateTime: ticket.plannedEndDateTime };
    setShowResizePreview(true);

    const onMove = (me) => {
      me.preventDefault();
      const deltaY = me.clientY - startPos.y;
      const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
      const minutesPerPixel = workingDayMinutes / parentHeight;
      const deltaMinutes = deltaY * minutesPerPixel;
      const originalStart = new Date(ticket.plannedStartDateTime);
      const originalEnd = new Date(ticket.plannedEndDateTime);

      let newStart = originalStart;
      let newEnd = originalEnd;
      if (handle === 'top') {
        const newStartMinutes = originalStart.getHours() * 60 + originalStart.getMinutes() + deltaMinutes;
        const snapped = snapToGrid(newStartMinutes);
        newStart = new Date(originalStart);
        newStart.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
      } else {
        const newEndMinutes = originalEnd.getHours() * 60 + originalEnd.getMinutes() + deltaMinutes;
        const snapped = snapToGrid(newEndMinutes);
        newEnd = new Date(originalEnd);
        newEnd.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
      }

      // Minimum 30 minutes
      if (newEnd - newStart < 30 * 60 * 1000) return;

      currentResizeValues.current = { startDateTime: newStart.toISOString(), endDateTime: newEnd.toISOString() };

      const tz = getUserTimeZone();
      const startTime = newStart.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: tz });
      const endTime = newEnd.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: tz });
      setResizePreview({ startTime, endTime, position: { x: me.clientX, y: me.clientY } });

      // Visual feedback
      setPlannedTickets(prev => prev.map(t => t.id === ticket.id ? ({ ...t, plannedStartDateTime: newStart.toISOString(), plannedEndDateTime: newEnd.toISOString() }) : t));
    };

    const onUp = async () => {
      cleanupResizeListeners();
      setShowResizePreview(false);
      setResizePreview(null);
      setJustResized(true);
      setTimeout(() => { setJustResized(false); setResizeMode(false); }, 150);

      setOperationInProgress(true);
      setOperationMessage(t('calendar.updating', 'Wird aktualisiert...'));
      try {
        const { startDateTime, endDateTime } = currentResizeValues.current || {};
        if (!startDateTime || !endDateTime) return;
        const payload = { startDateTime, endDateTime, isPlanned: true };
        if (currentUser) payload.assignedTo = currentUser.mail || currentUser.userPrincipalName;
        const ticketId = ticket.originalTicketId || ticket.id;
        await apiService.updateTicketPlanning(ticketId, payload);
        await loadDayDataSilently();
      } catch (e) {
        console.error('DayCalendarPanel: resize update error', e);
        setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
        await loadDayDataSilently();
      } finally {
        setOperationInProgress(false);
        setOperationMessage('');
      }
    };

    resizeMouseMoveRef.current = onMove;
    resizeMouseUpRef.current = onUp;
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
  };

  const handlePlannedTicketDragStart = (e, ticket) => {
    if (resizeMode || operationInProgress) { e.preventDefault(); return; }
    setDraggedPlannedTicket(ticket);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStartPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'planned-ticket', ticket }));
  };

  const handlePlannedTicketDragEnd = () => {
    setDraggedPlannedTicket(null);
    setDragStartPos(null);
    setShowDragPreview(false);
  };

  const calculateDragPreview = (e) => {
    if (!draggedPlannedTicket) return null;
    const dayColumn = e.currentTarget.querySelector('.core-hours-section');
    if (!dayColumn) return null;
    const rect = dayColumn.getBoundingClientRect();
    // Für externes Drag (Dashboard) haben wir kein internes dragStartPos → nutze Cursor direkt
    let relativeY = e.clientY - rect.top - ((dragStartPos && typeof dragStartPos.y === 'number') ? dragStartPos.y : 0);
    const topPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
    const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
    const totalMinutes = (topPercent / 100) * workingDayMinutes;
    const snappedMinutes = snapToGrid(totalMinutes);
    const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
    const newStartMinute = snappedMinutes % 60;
    const newStartTime = `${newStartHour.toString().padStart(2, '0')}:${newStartMinute.toString().padStart(2, '0')}`;

    // Height from original ticket duration
    let durationMinutes = 60;
    try {
      const s = draggedPlannedTicket.plannedStartDateTime ? new Date(draggedPlannedTicket.plannedStartDateTime) : null;
      const en = draggedPlannedTicket.plannedEndDateTime ? new Date(draggedPlannedTicket.plannedEndDateTime) : null;
      if (s && en) {
        const diff = Math.max(0, (en - s) / (1000 * 60));
        if (diff > 0) durationMinutes = diff;
      }
    } catch (_) {}
    const heightPercent = Math.max(2, Math.min(100, (durationMinutes / workingDayMinutes) * 100));
    return { topPercent, heightPercent, startTime: newStartTime };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // If dragging comes from Dashboard (external), synthesize a preview ticket so height can be computed
    try {
      if (!draggedPlannedTicket) {
        const raw = e.dataTransfer.getData('text/plain');
        const data = raw ? JSON.parse(raw) : null;
        if (data && data.type === 'dashboard-ticket') {
          const now = new Date();
          const end = new Date(now.getTime() + 60 * 60 * 1000); // default 60 min duration
          setDraggedPlannedTicket({
            id: `external:${data.ticketId || 'unknown'}`,
            plannedStartDateTime: now.toISOString(),
            plannedEndDateTime: end.toISOString(),
            title: (data.ticketData && data.ticketData.title) || 'Ticket',
            ticketNumber: data.ticketData && data.ticketData.ticketNumber
          });
        } else if (data && data.type === 'ticket') {
          // Ungeplantes Ticket aus Sidebar/anderem Kontext → Ghost mit Standarddauer erzeugen
          const now = new Date();
          // plannedHours berücksichtigen, falls vorhanden
          let durationMinutes = 60;
          try {
            const ph = parseFloat(data.ticket?.plannedHours);
            if (!Number.isNaN(ph) && ph > 0) durationMinutes = Math.round(ph * 60);
          } catch (_) {}
          const end = new Date(now.getTime() + durationMinutes * 60 * 1000);
          setDraggedPlannedTicket({
            id: `external:${data.ticket?.id || 'unknown'}`,
            plannedStartDateTime: now.toISOString(),
            plannedEndDateTime: end.toISOString(),
            title: (data.ticket && data.ticket.title) || 'Ticket',
            ticketNumber: data.ticket && data.ticket.ticketNumber
          });
        }
      }
    } catch (_) {}

    const preview = calculateDragPreview(e);
    if (preview) { setDragPreview(preview); setShowDragPreview(true); }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setShowDragPreview(false);
    if (operationInProgress) return;

    try {
      const raw = e.dataTransfer.getData('text/plain');
      const data = raw ? JSON.parse(raw) : null;
      if (!data || (data.type !== 'planned-ticket' && data.type !== 'dashboard-ticket' && data.type !== 'ticket')) return;
      const ticket = data.ticket || data.ticketData || null;
      const ticketId = data.ticketId || ticket?.id;
      if (!ticketId) return;
      const dayColumn = e.currentTarget.querySelector('.core-hours-section');
      const rect = dayColumn.getBoundingClientRect();
      let relativeY = e.clientY - rect.top - (dragStartPos?.y || 0);
      const heightPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
      const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
      const totalMinutes = (heightPercent / 100) * workingDayMinutes;
      const snappedMinutes = snapToGrid(totalMinutes);
      const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
      const newStartMinute = snappedMinutes % 60;
      let duration;
      if (data.type === 'planned-ticket' && ticket?.plannedStartDateTime && ticket?.plannedEndDateTime) {
        const originalStart = new Date(ticket.plannedStartDateTime);
        const originalEnd = new Date(ticket.plannedEndDateTime);
        duration = Math.max(30 * 60 * 1000, (originalEnd - originalStart));
      } else if (data.type === 'ticket') {
        // Ungeplantes Ticket: geplante Stunden berücksichtigen
        const ph = parseFloat(ticket?.plannedHours);
        duration = (!Number.isNaN(ph) && ph > 0) ? Math.round(ph * 60 * 60 * 1000) : (60 * 60 * 1000);
      } else if (data.type === 'dashboard-ticket') {
        // Dashboard-Tickets können geplant oder ungeplant sein
        if (ticket?.plannedStartDateTime && ticket?.plannedEndDateTime) {
          const originalStart = new Date(ticket.plannedStartDateTime);
          const originalEnd = new Date(ticket.plannedEndDateTime);
          duration = Math.max(30 * 60 * 1000, (originalEnd - originalStart));
        } else {
          const ph = parseFloat(ticket?.plannedHours);
          duration = (!Number.isNaN(ph) && ph > 0) ? Math.round(ph * 60 * 60 * 1000) : (60 * 60 * 1000);
        }
      } else {
        duration = 60 * 60 * 1000;
      }

      const newStartDateTime = new Date(dayStart);
      newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
      const newEndDateTime = new Date(newStartDateTime.getTime() + duration);

      // Bei geplanten Tickets: Dialog anzeigen (verschieben vs. weiteren erstellen)
      if (data.type === 'planned-ticket') {
        setDropChoiceModal({ visible: true, context: { ticket, newStartDateTime, duration } });
        return;
      }

      // Dashboard-Tickets: Wenn bereits geplant (z. B. überfällig), ebenfalls Modal anzeigen
      if (data.type === 'dashboard-ticket') {
        let resolvedTicket = ticket;
        let isPlanned = Boolean(resolvedTicket?.plannedStartDateTime || resolvedTicket?.plannedEndDateTime || resolvedTicket?.isPlanned);
        if (!isPlanned) {
          // Fallback: Ticket-Details laden, um Planungsstatus sicher festzustellen
          try {
            const all = await apiService.getTickets();
            const found = (all && all.tickets) ? all.tickets.find(t => t.id === ticketId) : null;
            if (found) {
              resolvedTicket = found;
              isPlanned = Boolean(found.plannedStartDateTime || found.plannedEndDateTime || found.isPlanned);
            }
          } catch (_) {}
        }
        if (isPlanned) {
          setDropChoiceModal({ visible: true, context: { ticket: resolvedTicket, newStartDateTime, duration, origin: data.origin || 'dashboard' } });
          return;
        }
      }

      // Ungeplante Tickets sofort planen
      setOperationInProgress(true);
      setOperationMessage(t('calendar.updating', 'Wird aktualisiert...'));

      if (data.type === 'ticket' || data.type === 'dashboard-ticket') {
        // Neue Planung anlegen
        const planningData = { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString() };
        if (currentUser) planningData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
        await apiService.planTicket(ticketId, planningData);
      }
      await loadDayDataSilently();
      // Dashboard sofort informieren: Ticket wurde geplant → direkt aus "Meine"/"Pool" entfernen
      try {
        window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'planned_from_day_panel', action: 'planned', tickets: [{ id: ticketId, isPlanned: true, plannedStartDateTime: newStartDateTime.toISOString(), plannedEndDateTime: newEndDateTime.toISOString(), assignedTo: (currentUser && (currentUser.mail || currentUser.userPrincipalName)) || undefined }] } }));
      } catch (_) {}
      // kein Dashboard-Refresh auslösen, um Rennbedingungen mit Server-Update zu vermeiden
    } catch (err) {
      console.error('DayCalendarPanel: drop error', err);
      setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
      await loadDayDataSilently();
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
      // Clear synthetic drag preview ticket
      try { setDraggedPlannedTicket(null); } catch (_) {}
    }
  };

  const timeSlots = useMemo(() => getTimeSlots(), [getTimeSlots]);

  const panelDateStr = useMemo(() => {
    try {
      const tz = getUserTimeZone();
      return new Date(dayStart).toLocaleDateString(getLocale(), { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz });
    } catch (_) { return ''; }
  }, [dayStart, getLocale, getUserTimeZone]);

  // Only show current time marker if the panel's day is today in user's timezone
  const isPanelToday = useMemo(() => {
    try { return isToday(dayStart); } catch (_) { return false; }
  }, [dayStart, isToday]);

  // Build all items for the day and calculate columns
  const positionedItems = useMemo(() => {
    // #308: dieselbe Auswahl gab es in vier Fassungen -- jetzt einmal, geprueft
    const allItems = tagesEintraege(events, plannedTickets, dayStart, dayEnd);
    return calculateItemPositions(filterDuplicateGraphEvents(allItems));
    // filterDuplicateGraphEvents steht bewusst NICHT in der Liste: die Funktion
    // entsteht bei jedem Render neu, waere das Memo also sofort wertlos. Sie
    // arbeitet hier ausschliesslich auf ihrem Argument -- anders als im
    // globalen Kalender, wo sie die Tickets aller Bearbeiter heranzieht.
  }, [events, plannedTickets, dayStart, dayEnd, calculateItemPositions, tagesEintraege]);

  if (loading) {
    if (isEmbedded) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, minHeight: 120 }}>
          <RefreshCw className="animate-spin" size={18} />
        </div>
      );
    }
    return (
      <div className="modal-content create-ticket-modal" style={{ maxWidth: 400, minWidth: 300, minHeight: '98vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Calendar size={24} />
            <h3 style={{ marginLeft: 8 }}>{t('calendar.loading', 'Lade Termine...')}</h3>
          </div>
        </div>
        <div className="form-body" style={{ padding: 0, flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ padding: 12, color: 'var(--color-text-muted)' }}>
            <RefreshCw className="animate-spin" size={18} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={isEmbedded ? '' : 'modal-content create-ticket-modal'}
      style={isEmbedded ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : { maxWidth: 400, minWidth: 300, minHeight: '98vh', display: 'flex', flexDirection: 'column' }}
    >
      {!isEmbedded && (
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Calendar size={24} />
            <h3 style={{ marginLeft: 8 }}>{t('dayCalendar.title', 'Eventtag')}</h3>
          </div>
        </div>
      )}
      <div className={isEmbedded ? '' : 'form-body'} style={isEmbedded ? { padding: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : { padding: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <div style={{ color: 'var(--color-danger)', padding: 8 }}>{error}</div>
        )}
        <div className="calendar-view" style={{ '--time-col-width': '64px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="calendar-grid" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {!isEmbedded && (
            <div 
              className="calendar-week-header" 
              style={{ display: 'grid', gridTemplateColumns: 'var(--time-col-width) 1fr', gap: '1px' }}
            >
              <div className="time-column-header">Zeit</div>
              <div className="day-header" style={{ minWidth: 0 }}>
                {panelDateStr}
              </div>
            </div>
            )}

            <div 
              className="calendar-week-body" 
              style={{ display: 'grid', gridTemplateColumns: 'var(--time-col-width) 1fr', gap: '1px', flex: 1, minHeight: 0 }}
            >
              <div className="time-column" style={{ width: 'var(--time-col-width)' }}>
                {timeSlots.map(hour => (
                  <div key={hour} className="time-slot" style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }}>
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                ))}
              </div>

              <div 
                className="day-column drop-zone" 
                style={{ position: 'relative' }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; handleDragOver(e); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setShowDragPreview(false); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e); }}
                title={operationInProgress ? `Warten Sie: ${operationMessage}` : ''}
              >
                {/* Above indicator */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (timeStartHour <= 1) { setTimeStartHour(8); } else { setTimeStartHour(1); }
                  }}
                  className="btn btn-xxs"
                  title={'Bis 1 Uhr erweitern'}
                  style={{ padding: '0 6px', fontSize: '10px', lineHeight: '12px', border: 'none', background: 'transparent', color: 'var(--color-text-faint)', cursor: 'pointer', position: 'absolute', top: '0', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 900, pointerEvents: 'auto' }}
                >
                  ▲
                </button>

                <div className="core-hours-section" style={{ position: 'relative', height: '100%' }}>
                  <div className="day-events" style={{ position: 'relative', height: '100%' }} onMouseDown={(e) => {
                    // Nur Linksklick, blockiere während Operation, ignoriere Klicks auf Elemente
                    if (operationInProgress || e.button !== 0) return;
                    if (e.target.closest && (
                      e.target.closest('.calendar-ticket') ||
                      e.target.closest('.calendar-event') ||
                      e.target.closest('.resize-handle')
                    )) return;

                    const section = e.currentTarget;
                    const rect = section.getBoundingClientRect();

                    setIsSelecting(true);
                    selectionContainerRectRef.current = rect;
                    selectionStartYRef.current = e.clientY - rect.top;
                    setSelectionRect({ topPercent: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)), heightPercent: 0 });

                    const moveHandler = (moveEvent) => {
                      if (!selectionContainerRectRef.current) return;
                      moveEvent.preventDefault();
                      const moveRect = selectionContainerRectRef.current;
                      const currentY = moveEvent.clientY - moveRect.top;
                      const startY = selectionStartYRef.current;
                      const top = Math.max(0, Math.min(startY, currentY));
                      const bottom = Math.max(startY, currentY);
                      const topPercent = Math.max(0, Math.min(100, (top / moveRect.height) * 100));
                      const heightPercent = Math.max(0, Math.min(100, ((bottom - top) / moveRect.height) * 100));
                      setSelectionRect({ topPercent, heightPercent });
                    };

                    const upHandler = (upEvent) => {
                      if (!selectionContainerRectRef.current) return;
                      document.removeEventListener('mousemove', selectionMouseMoveRef.current);
                      document.removeEventListener('mouseup', selectionMouseUpRef.current);
                      selectionMouseMoveRef.current = null;
                      selectionMouseUpRef.current = null;

                      const finRect = selectionContainerRectRef.current;
                      const endY = upEvent.clientY - finRect.top;
                      const startY = selectionStartYRef.current;
                      const topPx = Math.max(0, Math.min(startY, endY));
                      const bottomPx = Math.max(startY, endY);

                      const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
                      const startPercent = Math.max(0, Math.min(100, (topPx / finRect.height) * 100));
                      const endPercent = Math.max(0, Math.min(100, (bottomPx / finRect.height) * 100));
                      let startTotalMinutes = (startPercent / 100) * workingDayMinutes;
                      let endTotalMinutes = (endPercent / 100) * workingDayMinutes;

                      // snap auf 30 Minuten
                      startTotalMinutes = snapToGrid(startTotalMinutes);
                      endTotalMinutes = snapToGrid(endTotalMinutes);

                      // Minimum 30 Minuten
                      if (endTotalMinutes <= startTotalMinutes) {
                        endTotalMinutes = startTotalMinutes + 30;
                      }

                      const startHour = Math.floor(startTotalMinutes / 60) + timeStartHour;
                      const startMinute = startTotalMinutes % 60;
                      const endHour = Math.floor(endTotalMinutes / 60) + timeStartHour;
                      const endMinute = endTotalMinutes % 60;

                      const startDateTime = new Date(dayStart);
                      startDateTime.setHours(startHour, startMinute, 0, 0);
                      const endDateTime = new Date(dayStart);
                      endDateTime.setHours(endHour, endMinute, 0, 0);

                      setIsSelecting(false);
                      setSelectionRect(null);
                      selectionContainerRectRef.current = null;

                      setPendingPlannedTimeRange({
                        startDateTime: startDateTime.toISOString(),
                        endDateTime: endDateTime.toISOString()
                      });

                      // Modal zum Erstellen öffnen
                      setShowCreateTicketForm(true);
                    };

                    selectionMouseMoveRef.current = moveHandler;
                    selectionMouseUpRef.current = upHandler;
                    document.addEventListener('mousemove', moveHandler, { passive: false });
                    document.addEventListener('mouseup', upHandler);
                  }}>
                    {positionedItems.map((item, idx) => {
                      const posLeft = item.position?.left ?? 2;
                      const posWidth = item.position?.width ?? 'calc(100% - 4px)';
                      if (item.type === 'event') {
                        const event = item.data;
                        const status = getEventStatus(event.showAs);
                        const positionTop = (function(){
                          const p = getEventPosition(event); const h = getEventHeight(event);
                          try {
                            const startUtc = event.start.dateTime.endsWith('Z') ? event.start.dateTime : `${event.start.dateTime}Z`;
                            const start = new Date(startUtc);
                            const tz = getUserTimeZone();
                            const fmt = new Intl.DateTimeFormat('de-DE', { timeZone: tz, hour: 'numeric', hour12: false });
                            const startHour = parseInt(fmt.format(start));
                            if (startHour >= timeEndHour) return Math.max(0, 100 - h);
                            if (startHour < timeStartHour) return 0; 
                          } catch(_) {}
                          return p;
                        })();
                        const height = getEventHeight(event);
                        return (
                          <div
                            key={`event-${event.id}-${idx}`}
                            className="calendar-event calendar-event-positioned"
                            style={{ position: 'absolute', top: `${positionTop}%`, left: posLeft, width: posWidth, height: `${height}%`, backgroundColor: status.color, color: 'white', padding: '2px 4px', borderRadius: '3px', fontSize: '10px', lineHeight: '1.1', zIndex: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: (onOpenEvent || onOpenTicket) ? 'pointer' : 'default' }}
                            title={`${event.subject}\n${formatTime(event.start.dateTime)} - ${formatTime(event.end.dateTime)}\n${event.location?.displayName || ''}`}
                            onClick={() => {
                              try {
                                const maybeTicketId = event.__ticket?.id || event.__ticketInfo?.ticketId;
                                if (maybeTicketId && onOpenTicket) {
                                  onOpenTicket(event.__ticket || { id: maybeTicketId, ticketNumber: event.__ticketInfo?.ticketNumber });
                                  return;
                                }
                              } catch (_) {}
                              if (onOpenEvent) onOpenEvent(event);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEventContextMenu({ visible: true, x: e.clientX, y: e.clientY, event });
                            }}
                          >
                            <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {event.subject || t('global.noSubject', '(Kein Betreff)')}
                            </div>
                            <div style={{ fontSize: '8px', opacity: 0.9 }}>
                              {formatTime(event.start.dateTime)}
                            </div>
                            {event.location?.displayName && height > 15 && (
                              <div style={{ fontSize: '8px', opacity: 0.8, marginTop: '1px' }}>
                                <MapPin size={6} style={{ marginRight: '1px', verticalAlign: 'middle' }} />
                                {event.location.displayName.substring(0, 15)}
                              </div>
                            )}
                            {(() => {
                              try {
                                const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
                                const joinUrl = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
                                const isTeams = Boolean((event?.isOnlineMeeting && provider.includes('teams')) || (joinUrl && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(joinUrl)));
                                if (!isTeams) return null;
                                return (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '2px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '1px',
                                      zIndex: 30,
                                      pointerEvents: 'none',
                                      backgroundColor: 'var(--color-glass-90)',
                                      borderRadius: '3px',
                                      boxShadow: '0 1px 3px var(--color-shadow-30)'
                                    }}
                                    title={t('eventDetail.teamsMarker', 'Teams-Besprechung')}
                                  >
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        lineHeight: '12px',
                                        display: 'block',
                                        textAlign: 'center'
                                      }}
                                    >
                                      🎥
                                    </span>
                                  </div>
                                );
                              } catch (_) { return null; }
                            })()}
                          </div>
                        );
                      } else {
                        const ticket = item.data;
                        const isHighlighted = (
                          (highlightTicketId && (ticket.id === highlightTicketId || ticket.originalTicketId === highlightTicketId)) ||
                          (highlightTicketNumber && String(ticket.ticketNumber || '') === String(highlightTicketNumber))
                        );
                        const color = isHighlighted ? 'var(--color-search-hit)' : (ticket.isArchived ? getEventStatus('busy').color : getTicketColor(ticket));
                        const positionTop = (function(){
                          const p = getTicketPosition(ticket); const h = getTicketHeight(ticket);
                          try {
                            const start = new Date(ticket.plannedStartDateTime);
                            const tz = getUserTimeZone();
                            const fmt = new Intl.DateTimeFormat('de-DE', { timeZone: tz, hour: 'numeric', hour12: false });
                            const startHour = parseInt(fmt.format(start));
                            if (startHour >= timeEndHour) return Math.max(0, 100 - h);
                            if (startHour < timeStartHour) return 0; 
                          } catch(_) {}
                          return p;
                        })();
                        const height = getTicketHeight(ticket);
                        return (
                          <div
                            key={`ticket-${ticket.id}-${idx}`}
                            className={`calendar-event calendar-event-positioned calendar-ticket ${draggedPlannedTicket?.id === ticket.id ? 'dragging' : ''} ${operationInProgress ? 'operation-blocked' : ''} ${hatKennung(ticket) ? 'has-markers' : ''} ${ticket.isArchived ? 'archived-ticket' : ''}`}
                            style={{ position: 'absolute', top: `${positionTop}%`, left: posLeft, width: posWidth, height: `${height}%`, backgroundColor: color, color: 'white', padding: '2px 4px', paddingRight: kennungPlatz(ticket), borderRadius: '3px', fontSize: '10px', lineHeight: '1.1', cursor: 'pointer', zIndex: draggedPlannedTicket?.id === ticket.id ? 15 : 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '2px solid var(--color-glass-50)', opacity: draggedPlannedTicket?.id === ticket.id ? 0.5 : 1 }}
                            draggable={!resizeMode && !operationInProgress && !ticket.isArchived}
                            onDragStart={(e) => { e.stopPropagation(); if (ticket.isArchived) { e.preventDefault(); return; } handlePlannedTicketDragStart(e, ticket); }}
                            onDrag={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDragEnd={(e) => { e.preventDefault(); e.stopPropagation(); handlePlannedTicketDragEnd(); }}
                            onContextMenu={(e) => handleTicketRightClick(e, ticket)}
                            onClick={() => { if (justResized) return; if (onOpenTicket) onOpenTicket(ticket); }}
                            onRemove={handleRemoveFromPlanning}
                          >
                            {/* #604: Kopfzeile -- Marker, Teams, Prioritätspunkt ganz rechts */}
                            <KalenderTicketKennung ticket={ticket} />
                            {/* Resize handle top */}
                            <div
                              className="resize-handle resize-handle-top"
                              style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '5px', cursor: 'n-resize', backgroundColor: 'var(--color-glass-10)', zIndex: 30, borderTop: '1px solid var(--color-glass-30)' }}
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (ticket.isArchived) return; handleResizeStart(e, ticket, 'top'); }}
                              onDragStart={(e) => e.preventDefault()}
                              draggable={false}
                            />
                            <CalendarTicketTitle
                              ticket={ticket}
                              titleStyle={{ fontWeight: 'bold', fontSize: '10px', marginTop: '4px', marginBottom: '2px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', pointerEvents: 'none' }}
                            />
                            <div style={{ fontSize: '8px', opacity: 0.9, pointerEvents: 'none' }}>
                              {new Date(ticket.plannedStartDateTime).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', timeZone: getUserTimeZone() })}
                            </div>
                            {height > 15 && (
                              <div style={{ fontSize: '8px', opacity: 0.8, marginTop: '1px', marginBottom: '4px', pointerEvents: 'none' }}>
                                #{ticket.ticketNumber}
                              </div>
                            )}
                            {/* Resize handle bottom */}
                            <div
                              className="resize-handle resize-handle-bottom"
                              style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '5px', cursor: 's-resize', backgroundColor: 'var(--color-glass-10)', zIndex: 30, borderBottom: '1px solid var(--color-glass-30)' }}
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (ticket.isArchived) return; handleResizeStart(e, ticket, 'bottom'); }}
                              onDragStart={(e) => e.preventDefault()}
                              draggable={false}
                            />
                          </div>
                        );
                      }
                    })}

                    {/* Drag ghost */}
                    {draggedPlannedTicket && showDragPreview && dragPreview && typeof dragPreview.topPercent === 'number' && (
                      <div
                        className="calendar-event calendar-event-positioned calendar-ticket ghost"
                        style={{ position: 'absolute', top: `${dragPreview.topPercent}%`, left: 2, width: 'calc(100% - 4px)', height: `${dragPreview.heightPercent}%`, backgroundColor: getTicketColor(draggedPlannedTicket), color: 'white', padding: '2px 4px', borderRadius: '3px', fontSize: '10px', lineHeight: '1.1', border: '2px dashed var(--color-glass-90)', opacity: 0.7, zIndex: 14, pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}
                        title={`${draggedPlannedTicket.title}\n${dragPreview.startTime}`}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                          {draggedPlannedTicket.title}
                        </div>
                        <div style={{ fontSize: '8px', opacity: 0.9 }}>{dragPreview.startTime}</div>
                      </div>
                    )}

                    {/* Current time marker */}
                    {(() => {
                      if (!isPanelToday) return null;
                      const pos = getCurrentTimePosition(currentTime);
                      return pos != null ? (
                        <div 
                          className="current-time-marker"
                          style={{ position: 'absolute', top: `${pos}%`, left: 0, right: 0, height: 2, backgroundColor: 'var(--color-danger)', zIndex: 1000, boxShadow: '0 1px 3px var(--color-danger-shadow-30)', borderRadius: 1 }}
                        >
                          <div style={{ position: 'absolute', left: -6, top: -4, width: 10, height: 10, backgroundColor: 'var(--color-danger)', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px var(--color-shadow-20)' }} />
                          <div style={{ position: 'absolute', left: 8, top: -12, backgroundColor: 'var(--color-danger)', color: 'white', padding: '2px 6px', borderRadius: 3, fontSize: 11, fontWeight: 'bold', boxShadow: '0 2px 4px var(--color-shadow-20)', whiteSpace: 'nowrap' }}>
                            {currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: getUserTimeZone() })}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Time grid background */}
                    {timeSlots.map(hour => (
                      <div key={hour} className="time-slot-bg" style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }} />
                    ))}
                    {/* Auswahl-Rechteck (Selektion) */}
                    {isSelecting && selectionRect && (
                      <div
                        style={{ position: 'absolute', left: '2px', right: '2px', top: `${selectionRect.topPercent}%`, height: `${selectionRect.heightPercent}%`, backgroundColor: 'var(--color-cal-auswahl)', border: '2px dashed var(--color-cal-auswahl-rand)', borderRadius: '4px', pointerEvents: 'none', zIndex: 20 }}
                      />
                    )}
                  </div>
                </div>

                {/* Below indicator */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (timeEndHour >= 23) { setTimeEndHour(18); } else { setTimeEndHour(23); }
                  }}
                  className="btn btn-xxs"
                  title={'Bis 23 Uhr erweitern'}
                  style={{ padding: '0 6px', fontSize: '10px', lineHeight: '12px', border: 'none', background: 'transparent', color: 'var(--color-text-faint)', cursor: 'pointer', position: 'absolute', bottom: 0, left: '50%', transform: 'translate(-50%, 50%)', zIndex: 900, pointerEvents: 'auto' }}
                >
                  ▼
                </button>

              </div>
            </div>
          </div>
        </div>

        {/* OPERATION OVERLAY */}
        {operationInProgress && (
          <div className="operation-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--color-backdrop-10)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ background: 'var(--color-cal-drag-hinweis)', color: 'white', padding: '12px 16px', borderRadius: 8, boxShadow: '0 4px 20px var(--color-shadow-30)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 'bold' }}>
              <RefreshCw className="animate-spin" size={18} />
              {operationMessage}
            </div>
          </div>
        )}

        {/* LIVE RESIZE PREVIEW */}
        {showResizePreview && resizePreview && (
          <div 
            className="resize-preview"
            style={{ position: 'fixed', left: resizePreview.position.x + 10, top: resizePreview.position.y - 40, zIndex: 9999, background: 'var(--color-cal-resize-hinweis)', color: 'white', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold', boxShadow: '0 4px 12px var(--color-shadow-30)', pointerEvents: 'none', whiteSpace: 'nowrap' }}
          >
            ⏰ {resizePreview.startTime} - {resizePreview.endTime}
          </div>
        )}

    {/* CREATE TICKET MODAL */}
    {showCreateTicketForm && (
      <Modal
        open
        onClose={() => { setShowCreateTicketForm(false); setPendingPlannedTimeRange(null); setIsSelecting(false); setSelectionRect(null); }}
        title={<><Ticket size={24} /><h3>{t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}</h3><CalenderMarkerHeaderSelect value={newTicket.calenderMarker} onChange={(value) => setNewTicket({ ...newTicket, calenderMarker: value })} /></>}
        ariaLabel={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}
        footer={<>
          <button type="button" onClick={() => { setShowCreateTicketForm(false); setPendingPlannedTimeRange(null); setIsSelecting(false); setSelectionRect(null); }} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>
          <button type="submit" form="day-calendar-create-ticket-form" className="btn btn-primary" disabled={isCreatingTicket}><Plus size={16} />{isCreatingTicket ? t('common.creating', 'Erstelle...') : t('dashboard.createTicket', 'Ticket erstellen')}</button>
        </>}
      >

          <form id="day-calendar-create-ticket-form" onSubmit={async (e) => {
            e.preventDefault();
            if (isCreatingTicket) return;
            if (!newTicket.title.trim()) { flagInvalidField(e.currentTarget, '#ticket-title', t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.')) || pushToast({ type: 'error', message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.') }); return; }
            try {
              setIsCreatingTicket(true);
              const response = await apiService.createTicket(newTicket);
              const createdTicket = response.ticket || response;
              if (pendingPlannedTimeRange) {
                const planningData = {
                  startDateTime: pendingPlannedTimeRange.startDateTime,
                  endDateTime: pendingPlannedTimeRange.endDateTime
                };
                if (currentUser) planningData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
                try {
                  await apiService.planTicket(createdTicket.id, planningData);
                  setPendingPlannedTimeRange(null);
                  setIsSelecting(false);
                  setSelectionRect(null);
                  await loadDayDataSilently();
                } catch (planErr) {
                  console.error('DayCalendarPanel: error planning created ticket', planErr);
                  setError(t('calendar.planNewTicketError', 'Fehler beim Planen des neu erstellten Tickets'));
                }
              }
              setShowCreateTicketForm(false);
              setNewTicket({ title: '', description: '', priority: 'normal', category: '', tags: [], calenderMarker: null, customerId: null, contactId: null });
            } catch (error) {
              console.error('DayCalendarPanel: error creating ticket', error);
              pushToast({ type: 'error', message: t('calendar.createTicketError', 'Fehler beim Erstellen des Tickets.') });
            } finally {
              setIsCreatingTicket(false);
            }
          }} className="create-ticket-form">
            <div className="form-body">
              {/* #104: Kunde (halbbreit) über dem Titel */}
              <TicketCustomerField
                value={newTicket.customerId}
                onChange={(customerId) => setNewTicket(prev => ({ ...prev, customerId }))}
                contactValue={newTicket.contactId}
                onContactChange={(contactId) => setNewTicket(prev => ({ ...prev, contactId }))}
                inputId="ticket-customer"
              />
              <div className="form-group">
                <label htmlFor="ticket-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                <input id="ticket-title" type="text" value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} required placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')} className="form-input" />
              </div>
              <div className="form-group">
                <label htmlFor="ticket-description">{t('dashboard.field.description', 'Beschreibung')}</label>
                <textarea id="ticket-description" value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')} rows="4" className="form-textarea" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ticket-priority">{t('dashboard.field.priority', 'Priorität')}</label>
                  <select id="ticket-priority" value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })} className="form-select">
                    <option value="niedrig">Niedrig</option>
                    <option value="normal">Normal</option>
                    <option value="hoch">Hoch</option>
                    <option value="kritisch">Kritisch</option>
                  </select>
                </div>
              </div>
            </div>
          </form>
      </Modal>
    )}

        {/* DROP CHOICE MODAL */}
        {dropChoiceModal.visible && dropChoiceModal.context && (
          <Modal open onClose={() => setDropChoiceModal({ visible: false, context: null })} title={t('calendar.dropChoice.title', 'Termin verschieben oder neuen erstellen?')} maxWidth="560px">
                <p style={{ marginBottom: 12 }}>{t('calendar.dropChoice.text', 'Sie haben einen bestehenden Termin bewegt. Wie möchten Sie fortfahren?')}</p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      const ctx = dropChoiceModal.context;
                      if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                      setDropChoiceModal({ visible: false, context: null });
                      try {
                        setOperationInProgress(true);
                        setOperationMessage(t('calendar.updating', 'Wird aktualisiert...'));
                        const ticket = ctx.ticket;
                        const originalStart = new Date(ticket.plannedStartDateTime);
                        const originalEnd = new Date(ticket.plannedEndDateTime);
                        const duration = originalEnd - originalStart;
                        const newStartDateTime = new Date(ctx.newStartDateTime);
                        const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                        const planningUpdateData = { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString(), isPlanned: true };
                        if (currentUser) planningUpdateData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
                        await apiService.updateTicketPlanning(ticket.id, planningUpdateData);
                        await loadDayDataSilently();
                        // Dashboard sofort informieren: Ticket wurde verschoben/geplant → direkt aus Listen entfernen
                        try {
                          const origin = (ctx && ctx.origin) || null;
                          window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'planned_from_day_panel_move', origin, action: 'planned', tickets: [{ id: ticket.id }] } }));
                        } catch (_) {}
      // kein Dashboard-Refresh auslösen, um Rennbedingungen mit Server-Update zu vermeiden
                      } catch (err) {
                        console.error('DropChoice move error:', err);
                        setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
                      } finally {
                        setOperationInProgress(false);
                        setOperationMessage('');
                      }
                    }}
                  >
                    {t('calendar.dropChoice.move', 'Verschieben')}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      const ctx = dropChoiceModal.context;
                      if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                      setDropChoiceModal({ visible: false, context: null });
                      try {
                        setOperationInProgress(true);
                        setOperationMessage(t('calendar.creating', 'Erstelle...'));
                        const srcTicket = ctx.ticket;
                        // 1) Projekt ggf. anlegen (wenn noch keines verknüpft)
                        let projectId = null;
                        if ((srcTicket.projectId && Number(srcTicket.projectId) > 0) || (srcTicket.subprojectId && Number(srcTicket.subprojectId) > 0)) {
                          projectId = Number(srcTicket.projectId) || null;
                        } else {
                          const projectName = srcTicket.title || 'Projekt';
                          // #158/#230 (L3): Kunde des Quelltickets auf den Auto-Vorgang übernehmen (wie CalendarView)
                          const projectRes = await apiService.createProject({ name: projectName, description: '', responsibleEmail: null, status: 'wartend', turnusCount: null, turnusEvery: null, turnusUnit: null, subprojects: [], isAutoCreated: true, customerId: srcTicket.customerId || null });
                          projectId = projectRes?.project?.id;
                          if (!projectId) throw new Error('Projekt konnte nicht erstellt werden');
                        }
                        // 2) Originaltitel ggf. auf (1) normalisieren
                        const { baseTitle, existingBlock } = parseBlockSuffix(srcTicket.title);
                        const normalizedBase = baseTitle.replace(/\s*-\s*$/, '');
                        const shouldRenameOriginal = (existingBlock == null);
                        const newOriginalTitle = shouldRenameOriginal ? `${normalizedBase} - (1)` : srcTicket.title;
                        await apiService.updateTicket(srcTicket.id, { title: newOriginalTitle, projectId, subprojectId: null });
                        // 3) Zweites Ticket anlegen und am Ziel terminieren
                        const nextBlockNum = (existingBlock ? existingBlock + 1 : 2);
                        const block2Title = `${normalizedBase} - (${nextBlockNum})`;
                        const assignedTo = srcTicket.assignedTo || (currentUser?.mail || currentUser?.userPrincipalName || '');
                        const created2 = await apiService.createTicket({
                          title: block2Title,
                          description: srcTicket.description || '',
                          priority: srcTicket.priority || 'normal',
                          assignedTo: assignedTo || '',
                          category: srcTicket.category || 'Projekt',
                          tags: srcTicket.tags || [],
                          calenderMarker: srcTicket.calenderMarker || null,
                          projectId: projectId,
                          subprojectId: null,
                          // #158/#230 (L3): Kunde + Ansprechpartner explizit vererben (Server validiert den Kontakt)
                          customerId: srcTicket.customerId || null,
                          contactId: srcTicket.contactId || null
                        });
                        const block2Ticket = created2.ticket || created2;
                        const duration = ctx.duration || (60 * 60 * 1000);
                        const newStartDateTime = new Date(ctx.newStartDateTime);
                        const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                        const planningData = { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString(), assignedTo: assignedTo || undefined };
                        await apiService.planTicket(block2Ticket.id, planningData);
                        await loadDayDataSilently();
                      // Dashboard sofort informieren: Originalkarte nach Drop ausblenden
                      try {
                        window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'drop_choice_duplicate_day_panel', action: 'planned', tickets: [{ id: srcTicket.id }] } }));
                      } catch (_) {}
                        // kein Dashboard-Refresh auslösen, um Rennbedingungen mit Server-Update zu vermeiden
                      // kein Dashboard-Refresh auslösen, um Rennbedingungen mit Server-Update zu vermeiden
                      } catch (err) {
                        console.error('DropChoice duplicate error:', err);
                        setError(t('calendar.createError', 'Fehler beim Erstellen.'));
                      } finally {
                        setOperationInProgress(false);
                        setOperationMessage('');
                      }
                    }}
                  >
                    {t('calendar.dropChoice.createAnother', 'Weiteren erstellen')}
                  </button>
                </div>
          </Modal>
        )}
        {contextMenu.visible && (
          <div 
            className="context-menu"
            style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 10000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="context-menu-item"
              onClick={handleMoveToNextWeek}
              style={{ color: 'var(--color-primary)', fontWeight: 'bold' }}
            >
              📅 {t('contextMenu.moveNextWeek', 'Auf nächste Woche verschieben')}
            </div>
            {/* #604 */}
            <GebuchtMenuePunkt ticket={contextMenu.ticket} onClick={() => { const tk = contextMenu.ticket; setContextMenu({ visible: false, x: 0, y: 0, ticket: null }); gebuchtUmschalten(tk); }} />
            <div 
              className="context-menu-item"
              onClick={handleRemoveFromPlanning}
              style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}
            >
              🗑️ {t('global.removeFromPlanning', 'Aus Planung entfernen')}
            </div>
          </div>
        )}

        {/* EVENT CONTEXT MENU */}
        {eventContextMenu.visible && (
          <div
            className="context-menu"
            style={{ position: 'fixed', left: eventContextMenu.x, top: eventContextMenu.y, zIndex: 10000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="context-menu-item"
              onClick={() => {
                const ev = eventContextMenu.event;
                setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
                setSelectedEventForConvert(ev);
                setShowEventConvertModal(true);
              }}
              style={{ color: 'var(--color-primary)', fontWeight: 'bold' }}
            >
              📌 {t('calendar.convertToTicket', 'In Ticket umwandeln')}
            </div>
            <div
              className="context-menu-item"
              onClick={async () => {
                const ev = eventContextMenu.event;
                setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
                if (await confirm({ message: t('calendar.confirmDeleteEvent', 'Möchten Sie diesen Termin wirklich löschen?'), danger: true })) {
                  try {
                    setOperationInProgress(true);
                    setOperationMessage(t('calendar.deletingEvent', 'Termin wird gelöscht...'));
                    await apiService.deleteCalendarEvent(ev.id);
                    // Da DayCalendarPanel meist als Child verwendet wird und eigene loadDayDataSilently hat (als prop?? Nein, es ist intern definiert)
                    // Aber DayCalendarPanel hat eine interne loadEvents function die aufgerufen wird wenn date sich ändert.
                    // Wir müssen reload triggern. DayCalendarPanel scheint keine exposed reload method zu haben.
                    // Aber im onSuccess von EventToTicketModal wird loadDayDataSilently gerufen. Das nutzen wir.
                    await loadDayDataSilently();
                  } catch (err) {
                    console.error('Error deleting event:', err);
                    pushToast({ type: 'error', message: t('calendar.deleteError', 'Fehler beim Löschen des Termins.') });
                  } finally {
                    setOperationInProgress(false);
                    setOperationMessage('');
                  }
                }
              }}
              style={{ color: 'var(--color-danger)', fontWeight: 'bold', borderTop: '1px solid var(--color-border-faint)' }}
            >
              🗑️ {t('calendar.deleteEvent', 'Eintrag löschen')}
            </div>
          </div>
        )}

        {/* EVENT TO TICKET MODAL */}
        {showEventConvertModal && selectedEventForConvert && (
          <EventToTicketModal
            visible={showEventConvertModal}
            onClose={() => { setShowEventConvertModal(false); setSelectedEventForConvert(null); }}
            event={selectedEventForConvert}
            currentUser={currentUser}
            onSuccess={async () => { try { await loadDayDataSilently(); } catch (_) {} }}
          />
        )}
      </div>
    </div>
  );
};

export default DayCalendarPanel;


